import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/** A simulated media attachment (e.g. a document upload), matching the BE
 *  `media[]` shape in ourteam-backend#3712. */
export interface SimulateMediaInput {
    /** Publicly-fetchable URL of the file the BE will "receive". */
    url: string;
    /** MIME type, e.g. `application/pdf`, `image/jpeg`. */
    content_type: string;
}

/** Request body for `POST /whatsapp/simulate` (BE ourteam-backend#3712). */
export interface SimulateMayaRequest {
    /** Test candidate phone in E.164, e.g. `+60123456789`. */
    phone: string;
    /** The inbound message text. The FIRST message must carry `[REF:<job_id>]`
     *  so the BE onboards the test candidate onto that job's flow. */
    body: string;
    /** Optional WhatsApp profile name for the test candidate. */
    profile_name?: string;
    /** Optional simulated media (drives the documents step). */
    media?: SimulateMediaInput[];
}

/** Current flow position returned alongside Maya's replies. */
export interface SimulateFlowState {
    /** The step the candidate is currently on, e.g. `eligibility_check`. */
    current_step: string;
    /** Human-readable progress, e.g. `2/6`. */
    progress: string;
}

/** Response payload for `POST /whatsapp/simulate` (BE ourteam-backend#3712). */
export interface SimulateMayaResponse {
    /** Maya's reply text(s) — rendered as one bubble per entry. */
    replies: string[];
    /** The test candidate created/advanced by this turn. */
    candidate_id: string;
    /** Optional current flow step + progress. */
    flow?: SimulateFlowState;
}

/**
 * Drive one turn of the Maya chat simulator — admin-only.
 *
 * Contract (BE ourteam-backend#3712 — the endpoint runs the SAME inbound-processing
 * logic as the WhatsApp webhook but RETURNS Maya's reply text instead of sending it
 * via Twilio, so admins can test the chat-native hiring flow without a real number):
 *
 *   POST /whatsapp/simulate
 *     body { phone, body, profile_name?, media? }
 *     → { replies: string[], candidate_id, flow?: { current_step, progress } }
 *
 * NOTE: BE #3712 is not yet released to staging — the FE PR is held until it is
 * (per the FE worker BE-dependency gate). Field names come straight from the BE
 * ticket; none are invented. The envelope handling tolerates both the standard
 * `{ code, message, data }` wrapper and a bare payload, mirroring `sendWhatsappReply`.
 */
export async function simulateMayaChat(
    payload: SimulateMayaRequest
): Promise<SimulateMayaResponse> {
    const token = getCookie("authToken");
    try {
        const res = await axios.post(`${apiURL}/whatsapp/simulate`, payload, {
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            // The Maya turn runs synchronously on the BE (no Twilio), so allow room.
            timeout: 60000,
        });
        const body = res.data as {
            code?: number;
            message?: string;
            data?: SimulateMayaResponse;
            replies?: string[];
            candidate_id?: string;
            flow?: SimulateFlowState;
        };
        // Tolerate an in-envelope error code (HTTP 200 + code: 4xx).
        if (typeof body?.code === "number" && body.code >= 400) {
            throw new Error(body.message || "Maya simulate request failed.");
        }
        // Accept both the `{ data: … }` envelope and a bare payload.
        const data = body?.data ?? body;
        return {
            replies: Array.isArray(data.replies) ? data.replies : [],
            candidate_id: data.candidate_id ?? "",
            flow: data.flow,
        };
    } catch (error) {
        if (error instanceof Error) {
            catchErrorLog(`failed to simulate Maya chat, err: ${error.message}`);
        }
        throw error;
    }
}

/** One WhatsApp thread row for the simulator (BE ourteam-backend#3836). */
export interface SimulateThreadMessage {
    direction: "outbound" | "inbound";
    body: string;
    message_type?: string;
    /** ISO timestamp — the sort key and the `since` cursor for polling. */
    created_at: string;
    status?: string;
    /** Attachments on the message — the receptionist's service pictures (#4851).
     *  Each URL loads now: the backend presigns it afresh on every read. */
    media?: { url: string; contentType?: string }[];
}

/**
 * Read the test candidate's full WhatsApp thread for the Maya simulator —
 * admin/super_admin, READ-ONLY (#3171). Surfaces the async wa_call poller
 * messages (interview thank-you, no-answer skip #3824, retry/exhausted, "phone
 * interview in progress") that the synchronous `/whatsapp/simulate` reply doesn't
 * return, so the panel mirrors the real WhatsApp thread.
 *
 * Contract (BE ourteam-backend#3836 — NOT yet on staging; the FE poll is HELD
 * until it is, per the BE-dependency gate):
 *   GET /whatsapp/simulate/thread?phone=<e164>[&since=<iso8601>][&limit=200]
 *     → { code, data: [{ direction, body, message_type, created_at, status }] }  (oldest→newest)
 *   `since` returns only rows newer than that timestamp (cheap polling). The call
 *   does NOT mutate `hr_read`. Field names come straight from the BE ticket.
 */
export async function getMayaSimulateThread(
    phone: string,
    since?: string,
    limit = 200
): Promise<SimulateThreadMessage[]> {
    const token = getCookie("authToken");
    const res = await axios.get(`${apiURL}/whatsapp/simulate/thread`, {
        params: { phone, ...(since ? { since } : {}), limit },
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
    });
    const body = res.data as { code?: number; data?: SimulateThreadMessage[] };
    if (typeof body?.code === "number" && body.code >= 400) return [];
    const rows = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(res.data)
            ? (res.data as SimulateThreadMessage[])
            : [];
    return rows;
}

/** Request for the multipart file-upload turn (#3113). */
export interface SimulateUploadRequest {
    /** Test candidate phone in E.164 (the active conversation). */
    phone: string;
    /** The real file attached from the device (pdf/doc/docx/jpg/png). */
    file: File;
    /** Optional caption sent alongside the file (helps the BE match the doc item). */
    caption?: string;
}

/**
 * Upload a real file to the Maya chat simulator — the WhatsApp-style attach turn.
 *
 * Contract (BE ourteam-backend#3723 — multipart; stores to S3 and runs the same
 * document-matching path as a real WhatsApp media message, returning the same
 * reply shape as `/whatsapp/simulate`):
 *
 *   POST /whatsapp/simulate/upload   (multipart: phone, file, caption?)
 *     → { replies: string[], candidate_id, flow?: { current_step, progress } }
 *
 * NOTE: BE #3723 has no PR yet — the FE PR is held until it's released to staging.
 * Field names come straight from the BE ticket. Content-Type is left unset so the
 * browser adds the multipart boundary.
 */
export async function simulateMayaUpload(
    payload: SimulateUploadRequest
): Promise<SimulateMayaResponse> {
    const token = getCookie("authToken");
    const form = new FormData();
    form.append("phone", payload.phone);
    form.append("file", payload.file);
    if (payload.caption) form.append("caption", payload.caption);
    try {
        const res = await axios.post(`${apiURL}/whatsapp/simulate/upload`, form, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            // Upload + synchronous Maya turn — allow generous room.
            timeout: 120000,
        });
        const body = res.data as {
            code?: number;
            message?: string;
            data?: SimulateMayaResponse;
            replies?: string[];
            candidate_id?: string;
            flow?: SimulateFlowState;
        };
        if (typeof body?.code === "number" && body.code >= 400) {
            throw new Error(body.message || "Maya file upload failed.");
        }
        const data = body?.data ?? body;
        return {
            replies: Array.isArray(data.replies) ? data.replies : [],
            candidate_id: data.candidate_id ?? "",
            flow: data.flow,
        };
    } catch (error) {
        if (error instanceof Error) {
            catchErrorLog(`failed to upload to Maya simulator, err: ${error.message}`);
        }
        throw error;
    }
}
